import React, { useEffect, useState } from 'react';
import { Plus, Trash2, FolderOpen, FileDown, X, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useProjectStore, useLanguageStore, useAuthStore, getLocalized, useQuoteStore } from '../../store/index.js';
import { useTranslation } from '../../i18n/translations.js';
import { trackEvent } from '../../lib/analytics.js';

/**
 * ProjectSpecification — модуль «Спецификация проекта» (ТЗ §4.3).
 * Полностью синхронизирован с мультиязычной базой локализации FEDYK Furniture.
 */

const API = process.env.REACT_APP_BACKEND_URL || '';

const itemTitle = (item) => item.name || item.sku || 'System';

// Единый мультиязычный словарь токенов для дизайнерских ведомостей
const RAW_TAGS_MAP = {
  en: {
    wood: 'Solid Wood', mdf: 'MDF Panels', chipboard: 'Chipboard', melamine: 'Melamine', acrylic: 'Acrylic',
    aluminum: 'Aluminum Profile', glass: 'Premium Glass', mirror: 'Mirror', stone: 'Natural Stone',
    natural_veneer: 'Natural Veneer', engineered_veneer: 'Modified Veneer', hpl: 'HPL Panels',
    leather: 'Natural Leather', eco_leather: 'Eco Leather', fabric: 'Fabric / Textiles',
    primer: 'Primer', paint: 'Paint', lacquer: 'Lacquer',
    matte: 'Matte', glossy: 'Glossy', satin: 'Satin', textured: 'Textured', natural: 'Natural', brushed: 'Brushed'
  },
  ru: {
    wood: 'Натуральное Дерево', mdf: 'МДФ Панели', chipboard: 'ДСП / ЛДСП', melamine: 'Меламин', acrylic: 'Акрил',
    aluminum: 'Алюминиевый Профиль', glass: 'Премиальное Стекло', mirror: 'Зеркальное Полотно', stone: 'Натуральный Камень',
    natural_veneer: 'Натуральный Шпон', engineered_veneer: 'Модифицированный Шпон', hpl: 'HPL Панели',
    leather: 'Натуральная Кожа', eco_leather: 'Экокожа', fabric: 'Интерьерный Текстиль',
    primer: 'Архитектурный Грунт', paint: 'Полиуретановая Эмаль', lacquer: 'Защитный Лак',
    matte: 'Матовая', glossy: 'Глянцевая', satin: 'Сатинированная', textured: 'Текстурированная', natural: 'Натуральная фактура', brushed: 'Брашированная'
  },
  nl: {
    wood: 'Massief Hout', mdf: 'MDF Panelen', chipboard: 'Spaanplaat', melamine: 'Melamine', acrylic: 'Acryl',
    aluminum: 'Aluminium', glass: 'Premium Glas', mirror: 'Spiegel', stone: 'Natuursteen',
    natural_veneer: 'Natuurlijk Fineer', engineered_veneer: 'Gemodificeerd Fineer', hpl: 'HPL Panelen',
    leather: 'Natuurlijk Leer', eco_leather: 'Eco-leer', fabric: 'Stof / Textiel',
    primer: 'Primer', paint: 'Polyurethaanlak', lacquer: 'Beschermende Lak',
    matte: 'Mat', glossy: 'Glanzend', satin: 'Satijn', textured: 'Getextureerd', natural: 'Natuurlijk', brushed: 'Geborsteld'
  },
  ro: {
    wood: 'Lemn Masiv', mdf: 'Panouri MDF', chipboard: 'PAL', melamine: 'Melamină', acrylic: 'Acrilic',
    aluminum: 'Aluminiu', glass: 'Sticlă Premium', mirror: 'Oglindă', stone: 'Piatră Naturală',
    natural_veneer: 'Furnir Natural', engineered_veneer: 'Furnir Modificat', hpl: 'Panouri HPL',
    leather: 'Piele Naturală', eco_leather: 'Piele Ecologică', fabric: 'Material / Textile',
    primer: 'Grund', paint: 'Vopsea Poliuretanică', lacquer: 'Lac Protecție',
    matte: 'Mată', glossy: 'Lucioasă', satin: 'Satinată', textured: 'Texturată', natural: 'Naturală', brushed: 'Satinat'
  }
};

// Тексты локализации для управляющих кнопок интерфейса
const BUTTON_TRANSLATIONS = {
  en: {
    generating: 'Generating…',
    clientProposal: 'Client Proposal',
    tradePricing: 'Trade Pricing',
    submitting: 'SENDING...',
    submitBtn: 'SUBMIT TO FACTORY'
  },
  ru: {
    generating: 'Генерация…',
    clientProposal: 'КП для клиента',
    tradePricing: 'Трейд-калькуляция',
    submitting: 'ОТПРАВКА...',
    submitBtn: 'ОТПРАВИТЬ НА ФАБРИКУ'
  },
  nl: {
    generating: 'Genereren…',
    clientProposal: 'Klantvoorstel',
    tradePricing: 'Handelsprijzen',
    submitting: 'VERZENDEN...',
    submitBtn: 'VERZENDEN NAAR FABRIEK'
  },
  ro: {
    generating: 'Se generează…',
    clientProposal: 'Propunere Client',
    tradePricing: 'Prețuri Trade',
    submitting: 'SE TRIMITE...',
    submitBtn: 'TRIMITE LA FABRICĂ'
  }
};

const Field = ({ label, value, onChange, placeholder, className = '' }) => (
  <label className={`block text-left min-w-0 ${className}`}>
    <span className="text-[8.5px] sm:text-[9px] tracking-tight sm:tracking-[0.05em] uppercase text-[#9E9E9E] font-medium whitespace-nowrap block overflow-hidden text-ellipsis mb-1" title={label}>
      {label}
    </span>
    <input
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-2.5 sm:px-3 py-2 border border-[#ECECEC] bg-white text-sm focus:outline-none focus:border-[#0A0A0A] transition-colors rounded-none text-gray-900"
    />
  </label>
);

export const ProjectSpecification = () => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const currentTokens = RAW_TAGS_MAP[language] || RAW_TAGS_MAP['en'];
  const btnText = BUTTON_TRANSLATIONS[language] || BUTTON_TRANSLATIONS['en'];

  const {
    projects, activeProjectId, loading, fetchProjects, createProject, renameProject,
    removeProject, setActiveProject, addItemToProject, updateItem, removeItem, downloadOffer,
  } = useProjectStore();

  const { items: quoteItems, removeItem: removeQuoteItem } = useQuoteStore();

  const [newName, setNewName] = useState('');
  const [lineName, setLineName] = useState('');
  const [downloading, setDownloading] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const active = projects.find((p) => p.id === activeProjectId) || projects[0] || null;
  const l = t.projectSpec || {};

  // Каскадная синхронизация алертов
  const localAlerts = {
    itemRemoved: {
      en: 'Item removed from specification',
      ru: 'Изделие удалено из спецификации',
      nl: 'Item verwijderd uit specificatie',
      ro: 'Produs eliminat din specificație'
    },
    submitSuccess: {
      en: 'Project specification successfully transmitted to production!',
      ru: 'Спецификация успешно отправлена в работу! Отдел производства фабрики уведомлен.',
      nl: 'Projectspecificatie succesvol verzonden naar productie!',
      ro: 'Specificația proiectului a fost transmisă cu succes în producție!'
    },
    submitError: {
      en: 'Transmission error',
      ru: 'Не удалось отправить ведомость',
      nl: 'Fout bij verzenden',
      ro: 'Eroare de transmisie'
    }
  };

  const specDict = {
    noSystems: {
      en: 'No systems added yet. Type a system below, or use “Add to Project Spec” directly from any product page.',
      ru: 'Изделия еще не добавлены. Введите название системы ниже или нажмите «Добавить в спецификацию» прямо со страницы товара.',
      nl: 'Nog geen systemen toegevoegd. Typ hieronder een systeem of gebruik "Toevoegen aan projectspecificatie" direct vanaf een productpagina.',
      ro: 'Nu au fost adăugate sisteme încă. Tastați un sistem mai jos sau utilizați „Adăugă la specificația proiectului” direct de pe pagina produsului.'
    },
    errCreate: {
      en: 'Could not create project',
      ru: 'Не удалось создать проект',
      nl: 'Project aanmaken mislukt',
      ro: 'Nu s-a putut crea proiectul'
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreate = async () => {
    const defaultName = `${l.title || 'Project'} ${projects.length + 1}`;
    const targetName = newName.trim() || defaultName;
    const id = await createProject(targetName);
    if (!id) { 
      toast.error(specDict.errCreate[language] || 'Could not create project'); 
      return; 
    }
    setNewName('');
    trackEvent('b2b_project_created', { project_id: id });
  };

  const handleAddLine = () => {
    if (!active || !lineName.trim()) return;
    addItemToProject(active.id, { name: lineName.trim() });
    setLineName('');
  };

  const handleRemoveLineItem = (lineId, productId) => {
    if (!active) return;
    
    const targetedLine = active.items?.find((it) => it.line_id === lineId);
    removeItem(active.id, lineId);
    
    if (targetedLine) {
      const matchedQuoteItem = quoteItems.find((qItem) => {
        const qId = String(qItem.product?.id || '');
        const pId = String(productId || targetedLine.product_id || '');
        
        const idMatch = pId && qId === pId;
        const skuMatch = targetedLine.sku && qItem.product?.sku === targetedLine.sku;
        const nameMatch = targetedLine.name && getLocalized(qItem.product?.name, language) === targetedLine.name;
        
        return idMatch || skuMatch || nameMatch;
      });
      
      if (matchedQuoteItem) {
        removeQuoteItem(matchedQuoteItem.product.id);
      } else if (productId) {
        removeQuoteItem(productId);
        removeQuoteItem(String(productId));
      }
    } else if (productId) {
      removeQuoteItem(productId);
    }
    
    toast.success(localAlerts.itemRemoved[language] || localAlerts.itemRemoved['en']);
  };

  const handleSendToFactory = async () => {
    if (!active || (active.items?.length || 0) === 0) return;
    setIsSubmitting(true);

    const fallbackMat = { en: 'Bespoke selection', ru: 'Индивидуальный выбор', nl: 'Maatwerk selectie', ro: 'Selecție personalizată' };
    const fallbackFinish = { en: 'According to project specification', ru: 'Согласно спецификации проекта', nl: 'Volgens projectspecificatie', ro: 'Conform specificațiilor proiectului' };
    const fallbackAddress = { en: 'Specified in account profile', ru: 'Указан в личном профиле', nl: 'Gespecificeerd in accountprofiel', ro: 'Specificat în profilul contului' };

    try {
      const formattedItems = active.items.map(it => {
        const profileStr = it.configuration?.profile ? `Профиль: ${it.configuration.profile}` : '';
        const glassStr = it.configuration?.glass ? `Стекло: ${it.configuration.glass}` : '';
        const heightStr = it.configuration?.height ? `Высота: ${it.configuration.height}` : '';
        const baseFinish = it.custom_finish || '';
        
        const configSummary = [baseFinish, profileStr, glassStr, heightStr].filter(Boolean).join(', ');
        const matValue = it.custom_material || it.configuration?.materials || fallbackMat[language] || fallbackMat['en'];
        const finValue = configSummary || fallbackFinish[language] || fallbackFinish['en'];
        
        return {
          product_id: it.product_id || null,
          name: it.name || 'Bespoke System',
          sku: it.sku || 'CUSTOM',
          quantity: it.quantity || 1,
          price: it.price || 0,
          custom_material: matValue,
          custom_finish: finValue,
          selected_material: matValue,
          selected_finish: finValue
        };
      });

      const safeEmail = user?.email && user.email.includes('@') ? user.email : 'b2b@fedykfurniture.com';
      const safePhone = user?.phone && user.phone.replace(/[^0-9+]/g, '').length > 5 ? user.phone : '+3200000000'; 
      const safeAddress = `${user?.company_street || ''} ${user?.company_city || ''}`.trim() || fallbackAddress[language] || fallbackAddress['en'];

      const response = await fetch(`${API}/api/quotes?lang=${language}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          items: formattedItems,
          customer_name: `${user?.first_name || ''} ${user?.last_name || ''} (${user?.company_name || 'B2B Partner'})`.trim() || 'B2B Professional Partner',
          customer_email: safeEmail,
          customer_phone: safePhone,
          delivery_address: safeAddress,
          notes: `B2B Digital Specification Request for Project: "${active.name}"`
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server error code ${response.status}`);
      }

      toast.success(localAlerts.submitSuccess[language] || localAlerts.submitSuccess['en']);
      trackEvent('b2b_project_submitted_to_production', { project_id: active.id });
    } catch (err) {
      console.error('B2B submission error:', err);
      const baseErrTitle = localAlerts.submitError[language] || localAlerts.submitError['en'];
      toast.error(`${baseErrTitle}: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = async (mode) => {
    if (!active) return;
    setDownloading(mode);
    try {
      await downloadOffer(active.id, mode, language);
      trackEvent('b2b_commercial_offer_downloaded', { project_id: active.id, items: active.items.length, mode });
    } catch (e) {
      toast.error(`CRITICAL SYSTEM LOG — ${e.message || e}`, { duration: 15000 });
    } finally {
      setDownloading(null);
    }
  };

  const updateConfig = (lineId, key, value, current) =>
    updateItem(active.id, lineId, { configuration: { ...(current || {}), [key]: value } });

  return (
    <div data-testid="b2b-project-specification" className="text-left font-light select-none">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E9E9E] mb-2 font-medium whitespace-nowrap">
            {l.eyebrow || 'Specification · No online cart'}
          </p>
          <h2 className="text-3xl font-extralight tracking-tight text-[#0A0A0A] whitespace-nowrap">
            {l.title || 'Project Specification'}
          </h2>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-4 space-y-4">
          <div className="flex flex-col gap-2">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
              placeholder={l.placeholder || "e.g. Villa in Belgium"}
              className="w-full px-3 py-2 border border-[#ECECEC] bg-white text-sm focus:outline-none focus:border-[#0A0A0A] rounded-none h-11 text-gray-900"
              data-testid="project-new-name"
            />
            <button
              type="button"
              onClick={handleCreate}
              className="w-full inline-flex items-center justify-center bg-[#0A0A0A] text-white text-[10px] tracking-[0.2em] uppercase hover:bg-neutral-800 transition-colors font-medium rounded-none whitespace-nowrap h-11"
              data-testid="project-create"
            >
              <Plus className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> {l.new || 'New'}
            </button>
          </div>

          {loading && projects.length === 0 ? (
            <p className="text-sm font-light text-[#4A4A4A]">{l.loading || 'Loading projects…'}</p>
          ) : projects.length === 0 ? (
            <p className="text-sm font-light text-[#4A4A4A] border border-dashed border-[#ECECEC] p-6 text-center bg-white">
              {l.empty || 'No projects yet. Create one to start compiling systems.'}
            </p>
          ) : (
            <ul className="space-y-2">
              {projects.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => setActiveProject(p.id)}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-3 border text-left transition-all duration-300 rounded-none group ${
                      active?.id === p.id 
                        ? 'border-[#0A0A0A] bg-neutral-50 text-black font-medium' 
                        : 'border-[#ECECEC] bg-white text-gray-900'
                    } hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A]`}
                    data-testid={`project-item-${p.id}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <FolderOpen className="w-4 h-4 text-[#9E9E9E] shrink-0 group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                      <span className="truncate text-sm whitespace-nowrap group-hover:text-white transition-colors duration-300">{p.name}</span>
                    </span>
                    <span className="text-[10px] text-[#9E9E9E] tabular-nums shrink-0 group-hover:text-white/80 transition-colors duration-300">{p.items?.length || 0}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="lg:col-span-8">
          {!active ? (
            <div className="border border-dashed border-[#ECECEC] p-16 text-center text-sm font-light text-[#4A4A4A] bg-white">
              {l.selectProject || 'Select or create a project to compile its specification.'}
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-between gap-4 border-b border-gray-100 pb-3">
                <input
                  value={active.name}
                  onChange={(e) => renameProject(active.id, e.target.value)}
                  className="text-xl font-light tracking-tight bg-transparent border-b border-transparent hover:border-[#ECECEC] focus:border-[#0A0A0A] focus:outline-none w-full max-w-md rounded-none text-gray-900 truncate whitespace-nowrap"
                  data-testid="project-rename"
                />
                <button
                  type="button"
                  onClick={() => removeProject(active.id)}
                  className="shrink-0 text-[#9E9E9E] hover:text-red-600 transition-colors"
                  title="Delete project"
                  data-testid="project-delete"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>

              {/* Elements list */}
              {(active.items?.length || 0) === 0 ? (
                <p className="text-sm font-light text-[#4A4A4A] border border-dashed border-[#ECECEC] p-6 bg-white">
                  {specDict.noSystems[language] || 'No systems added yet. Type a system below, or use “Add to Project Spec” directly from any product page.'}
                </p>
              ) : (
                <div className="space-y-4">
                  {active.items.map((it) => (
                    <div key={it.line_id} className="border border-[#ECECEC] bg-white p-5 rounded-none shadow-sm text-left" data-testid={`spec-line-${it.line_id}`}>
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="min-w-0">
                          <p className="font-medium text-[#0A0A0A] truncate whitespace-nowrap text-base">{itemTitle(it)}</p>
                          {it.sku && <p className="text-[11px] text-[#9E9E9E] font-mono tracking-wide mt-0.5 whitespace-nowrap">{it.sku}</p>}
                          
                          {(it.custom_material || it.custom_finish || it.configuration?.materials || it.configuration?.finishes) && (
                            <div className="mt-2 space-y-1 text-[11px] bg-neutral-50/50 p-2 border border-gray-100 font-light max-w-md">
                              {(it.custom_material || it.configuration?.materials) && (
                                <p className="text-gray-800 flex items-center gap-1">
                                  <span className="text-[9px] text-neutral-400 font-semibold uppercase tracking-wider whitespace-nowrap">Pre-selected Material:</span>
                                  <span className="whitespace-nowrap truncate">{currentTokens[it.custom_material || it.configuration?.materials] || it.custom_material || it.configuration?.materials}</span>
                                </p>
                              )}
                              {(it.custom_finish || it.configuration?.finishes) && (
                                <p className="text-gray-800 flex items-center gap-1">
                                  <span className="text-[9px] text-neutral-400 font-semibold uppercase tracking-wider whitespace-nowrap">Pre-selected Finish:</span>
                                  <span className="whitespace-nowrap truncate">{currentTokens[it.custom_finish || it.configuration?.finishes] || it.custom_finish || it.configuration?.finishes}</span>
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveLineItem(it.line_id, it.product_id)}
                          className="text-[#9E9E9E] hover:text-red-600 transition-colors shrink-0 p-1"
                          data-testid={`spec-remove-${it.line_id}`}
                        >
                          <X className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                      
                      {/* Inputs grid with optimized column ratios */}
                      <div className="grid grid-cols-2 md:grid-cols-12 gap-3 sm:gap-4 pt-1">
                        <Field label={l.profile || 'Profile'} value={it.configuration?.profile} placeholder="e.g. Bronze" onChange={(v) => updateConfig(it.line_id, 'profile', v, it.configuration)} className="md:col-span-3" />
                        <Field label={l.glass || 'Glass'} value={it.configuration?.glass} placeholder="e.g. Graphite" onChange={(v) => updateConfig(it.line_id, 'glass', v, it.configuration)} className="md:col-span-3" />
                        <Field label={l.height || 'Height'} value={it.configuration?.height} placeholder="e.g. 2700 mm" onChange={(v) => updateConfig(it.line_id, 'height', v, it.configuration)} className="md:col-span-4" />
                        <label className="block text-left min-w-0 md:col-span-2">
                          <span className="text-[8.5px] sm:text-[9px] tracking-tight sm:tracking-[0.05em] uppercase text-[#9E9E9E] font-medium whitespace-nowrap block overflow-hidden text-ellipsis mb-1" title={l.qty || 'Qty'}>
                            {l.qty || 'Qty'}
                          </span>
                          <input
                            type="number"
                            min="1"
                            value={it.quantity || 1}
                            onChange={(e) => updateItem(active.id, it.line_id, { quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                            className="w-full px-2.5 sm:px-3 py-2 border border-[#ECECEC] bg-white text-sm focus:outline-none focus:border-[#0A0A0A] rounded-none text-gray-900"
                          />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <input
                  value={lineName}
                  onChange={(e) => setLineName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddLine()}
                  placeholder={l.placeholderAdd || "Add a system (e.g. INTEGRA sliding partition)"}
                  className="flex-1 px-3 py-2 border border-[#ECECEC] bg-white text-sm focus:outline-none focus:border-[#0A0A0A] rounded-none text-gray-900"
                  data-testid="spec-add-name"
                />
                <button
                  type="button"
                  onClick={handleAddLine}
                  className="shrink-0 inline-flex items-center justify-center px-5 border border-[#0A0A0A] text-[#0A0A0A] text-[10px] tracking-[0.2em] uppercase hover:bg-[#0A0A0A] hover:text-white transition-colors font-medium rounded-none h-11 whitespace-nowrap"
                  data-testid="spec-add-line"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} /> {l.add || 'Add'}
                </button>
              </div>

              {/* Single-line action buttons */}
              <div className="pt-6 border-t border-[#ECECEC] flex flex-col sm:flex-row gap-3 w-full">
                <button
                  type="button"
                  onClick={() => handleDownload('client')}
                  disabled={(active.items?.length || 0) === 0 || downloading !== null || isSubmitting}
                  className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 h-12 border border-[#0A0A0A] bg-transparent text-[#0A0A0A] text-[10px] sm:text-[11px] tracking-[0.12em] uppercase hover:bg-[#0A0A0A] hover:text-white transition-all duration-300 font-medium disabled:opacity-40 disabled:cursor-not-allowed rounded-none flex-1 whitespace-nowrap min-w-0"
                  data-testid="project-download-offer"
                >
                  {downloading === 'client' ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <FileDown className="w-4 h-4 shrink-0" />}
                  <span className="whitespace-nowrap">{downloading === 'client' ? btnText.generating : btnText.clientProposal}</span>
                </button>
                
                <button
                  type="button"
                  onClick={() => handleDownload('trade')}
                  disabled={(active.items?.length || 0) === 0 || downloading !== null || isSubmitting}
                  className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 h-12 border border-[#0A0A0A] bg-transparent text-[#0A0A0A] text-[10px] sm:text-[11px] tracking-[0.12em] uppercase hover:bg-[#0A0A0A] hover:text-white transition-all duration-300 font-medium disabled:opacity-40 disabled:cursor-not-allowed rounded-none flex-1 whitespace-nowrap min-w-0"
                  data-testid="project-download-trade"
                >
                  {downloading === 'trade' ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <FileDown className="w-4 h-4 shrink-0" />}
                  <span className="whitespace-nowrap">{downloading === 'trade' ? btnText.generating : btnText.tradePricing}</span>
                </button>

                <button
                  type="button"
                  onClick={handleSendToFactory}
                  disabled={(active.items?.length || 0) === 0 || downloading !== null || isSubmitting}
                  className="inline-flex items-center justify-center gap-2 px-3 sm:px-4 h-12 border border-[#0A0A0A] bg-transparent text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-all duration-300 font-medium text-[10px] sm:text-[11px] tracking-[0.12em] uppercase disabled:opacity-40 disabled:cursor-not-allowed rounded-none flex-1 whitespace-nowrap min-w-0"
                  data-testid="project-submit-to-factory"
                >
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin shrink-0" /> : <Send className="w-3.5 h-3.5 shrink-0" />}
                  <span className="whitespace-nowrap">{isSubmitting ? btnText.submitting : btnText.submitBtn}</span>
                </button>
              </div>
              
              <p className="text-[11px] text-[#9E9E9E] font-light leading-relaxed pt-1">
                {l.footerDesc || '“Client Proposal” shows retail figures only — safe to forward to the end client. “Trade Pricing” shows your net price with the professional discount applied.'}
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ProjectSpecification;
